import { useFormikContext } from "formik";
import React, { useState } from "react";
import classnames from "classnames";

import { TFormValues } from "./types";

import conditionsChecker from "../../util/conditionsChecker";
import { GuideContext } from "../../components/GatewayForm/GatewayForm.store";
import { useAnalytics } from "../../util/useAnalytics";

import QuestionRadioGroup from "../Form/QuestionRadioGroup";
import QuestionCheckboxGroup from "../Form/QuestionCheckboxGroup";
import QuestionSelect from "../Form/QuestionSelect";
import QuestionText from "../Form/QuestionText";
import QuestionTextarea from "../Form/QuestionTextarea";
import DateInput from "../Form/DateInput";
import AddressInput from "../Form/AddressInput";
import StepNavigation from "./StepNavigation";
import ArrowLeft from "../../assets/arrow-left.svg";
import CloseCross from "../../assets/cross.svg";

import styles from "./GuideStep.module.scss";

type TGuideStepProps = {};

const GuideStep: React.FC<TGuideStepProps> = () => {
  const { currentStep, currentConfig, setCurrentStep } = React.useContext(GuideContext);
  const { values, errors } = useFormikContext<TFormValues>();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const config = React.useMemo(() => {
    return currentConfig.steps[currentStep];
  }, [currentConfig.steps, currentStep]);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handlePreviousButton = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (config.previous === undefined) {
      return;
    }
    let goToValue = config.previous;
    if (typeof goToValue === "function") {
      goToValue = goToValue(values);
    }

    if (typeof goToValue === "string" && goToValue.startsWith("/")) {
      // window.location.href = goToValue;
      setCurrentStep("");
    } else {
      setCurrentStep(goToValue);
    }

    scrollToTop();
  };

  const scrollToTop = () => {
    if (scrollRef.current !== null) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const handleSubmit = (values: TFormValues) => {
    setSubmitLoading(true);

    currentConfig
      .onSubmit(values)
      .then(() => {
        if (config.next === undefined) {
          return;
        }
        // Next step may be a function with extra conditions
        let goToValue = config.next;
        if (typeof goToValue === "function") {
          goToValue = goToValue(values);
        }

        // Next step may be a link
        if (typeof goToValue === "string" && goToValue.startsWith("/")) {
          window.sessionStorage.setItem("currentFormStep", "");

          // Clear out the session storage
          // window.sessionStorage.setItem("formValues", "");
          window.location.href = goToValue;
        } else {
          setCurrentStep(goToValue);
        }
        scrollToTop();
      })
      .catch((e) => {
        setSubmitError(true);
      })
      .finally(() => setSubmitLoading(false));
  };

  if (!config) {
    return <></>;
  }

  if (config.type === "content") {
    return <>{config.component}</>;
  }

  return (
    <div
      onClick={(e) => {
        setCurrentStep("");
      }}
      ref={scrollRef}
      className={styles.formScrollWrapper}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={classnames(styles.wrapper, "gatewayFormWrapper animateIn container")}
      >
        <div className="buttonRow">
          {config.previous && (
            <button className="backButton" onClick={handlePreviousButton}>
              <img src={ArrowLeft} alt="back-arrow" />
              Back
            </button>
          )}
          <button className="closeButton" onClick={() => setCurrentStep("")}>
            <img src={CloseCross} alt="close-cross" />
            <span className="textWrapper">Close</span>
          </button>
        </div>

        <div className="gatewayFormContent">
          <h2>{config.title}</h2>
          {React.isValidElement(config.note) ? (
            <p>{config.note}</p>
          ) : (
            <p dangerouslySetInnerHTML={{ __html: config.note }} />
          )}
          {config.type === "form" &&
            config.components.map((c) => {
              // check if component should show
              if (c.condition && !conditionsChecker(c.condition, values)) {
                return null;
              }

              if (c.type === "text" || c.type === "email" || c.type === "tel") {
                return (
                  <QuestionText
                    key={c.name}
                    name={c.name}
                    label={c.label}
                    note={c.note}
                    error={errors[c.name] !== undefined}
                    inputMode={c.type}
                  />
                );
              }

              if (c.type === "postcode") {
                return (
                  <QuestionText
                    key={c.name}
                    name={c.name}
                    label={c.label}
                    note={c.note}
                    error={errors[c.name] !== undefined}
                    inputMode="numeric"
                  />
                );
              }

              if (c.type === "textarea") {
                return (
                  <QuestionTextarea
                    characterCount={1000}
                    key={c.name}
                    name={c.name}
                    label={c.label}
                    note={c.note}
                    error={errors[c.name] !== undefined}
                  />
                );
              }

              if (c.type === "address") {
                return (
                  <AddressInput
                    key={c.name}
                    name={c.name}
                    label={c.label}
                    note={c.note}
                    error={errors[c.name] !== undefined}
                  />
                );
              }

              if (c.type === "radio") {
                return (
                  <QuestionRadioGroup
                    key={c.name}
                    name={c.name}
                    label={c.label}
                    note={c.note}
                    options={c.options}
                    error={errors[c.name] !== undefined}
                  />
                );
              }

              if (c.type === "checkbox") {
                return (
                  <QuestionCheckboxGroup
                    key={c.name}
                    name={c.name}
                    label={c.label}
                    note={c.note}
                    options={c.options}
                    error={errors[c.name] !== undefined}
                  />
                );
              }

              if (c.type === "select") {
                return <QuestionSelect name={c.name} label={c.label} key={c.name} options={c.options} />;
              }

              if (c.type === "date") {
                return (
                  <DateInput
                    key={c.name}
                    name={c.name}
                    label={c.label}
                    note={c.note}
                    error={errors[c.name] !== undefined}
                  />
                );
              }
              return null;
            })}
          {config.type === "form-content" && config.component}
        </div>
        <div style={{ color: "#d82727", marginTop: 20 }}>
          {submitError && <p>There was an error when submitting the form</p>}
        </div>
        <StepNavigation
          onSubmit={handleSubmit}
          next={config.next}
          previous={config.previous}
          submit={config.submit}
          stepText={config.stepText}
          customButtonText={config.customButtonText}
          scrollToTop={scrollToTop}
          submitLoading={submitLoading}
        />
        {/* <pre>{JSON.stringify(values, null, 2)}</pre>
        <pre>{JSON.stringify(errors, null, 2)}</pre> */}
      </div>
    </div>
  );
};

export default GuideStep;
